import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { DateConstants, IconDefinitionConstants } from "utils/constants";
import { Amount } from "models/utilTypes/currencyType";
import { StyleEnums } from "models/enums";
import { ReportHanlder, ReportProps } from "models/propTypes/accountingPropTypes";
import { NestedTableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { DrillDownTable, MonthRangeSelectorBubble, useDownload } from "components/common";
import useFinancialReportRow from "../hooks/useFinancialReportRow";
import useApplyExpandAllRows from "../hooks/useApplyExpandAllRows";
import useStandardReportHelpers from "../hooks/useStandardReportHelpers";
import useTransactionAmount from "../hooks/useTransactionAmount";
import PrintStandardBalanceReport from "./print/standard";
import StandardBalanceReportCharts from "./standardCharts";
import { useFinancialReportService } from "serviceHooks";
import { useFinancialReportPermissions } from "components/accounting";
//#endregion
import { DateHelper, ExportHelper, FinancialReportHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const StandardBalanceReport: React.ForwardRefRenderFunction<ReportHanlder, ReportProps> = (
  { updateReportExportData, reloadKey, reload },
  ref
) => {
  const financialReportService = useFinancialReportService();
  const { hasChartsPermissions } = useFinancialReportPermissions();

  const {
    result,
    filter,
    primaryColumnQuickSelections,
    comparisonColumnQuickSelections,
    onRepeatColumnFilterChange,
    onRepeatColumnDelete,
    getRange,
    getSelection,
    loading,
    pageFilter,
  } = useStandardReportHelpers(financialReportService.getBalanceReport);

  const { handleDownloadPDFRender } = useDownload();

  const tableRef = React.useRef<NestedTableHandles>(null);

  React.useImperativeHandle(ref, () => ({
    print: (title?: string, subtitle?: string, fileName?: string) => {
      handleDownloadPDFRender(
        <PrintStandardBalanceReport
          title={title}
          subtitle={subtitle}
          result={result}
          getRange={getRange}
          ibHeaderDescription={ibHeaderDescription}
          ubHeaderDescription={ubHeaderDescription}
          showAccountNumber={pageFilter?.showAccountNumber ?? false}
        />,
        `${fileName ?? ""}.pdf`
      );
    },
    expandAll: () => tableRef.current?.expandAll(),
    collapseAll: () => tableRef.current?.collapseAll(),
  }));

  const ibHeaderDescription = (repeatIndex: number) => {
    if (filter?.columns) {
      if (repeatIndex >= filter.columns.length) return "";
      return filter.columns[repeatIndex].range?.start
        ? LocalizationHelperInstance.getFormattedDate(
            DateHelper.getFirstDayOfMonth(filter.columns[repeatIndex].range?.start ?? ({} as Date)),
            DateConstants.DateMonthYearShortConfig
          )
        : "";
    }
    return "";
  };

  const ubHeaderDescription = (repeatIndex: number) => {
    if (filter?.columns) {
      if (repeatIndex >= filter.columns.length) return "";
      return filter.columns[repeatIndex].range?.end
        ? LocalizationHelperInstance.getFormattedDate(
            DateHelper.getLastDayOfMonth(filter.columns[repeatIndex].range?.end ?? ({} as Date)),
            DateConstants.DateMonthYearShortConfig
          )
        : "";
    }
    return "";
  };

  const ibFormatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(undefined, row.iB);

  const amountFormatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(undefined, row.amount);

  const ubFormatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(undefined, row.uB);

  const { descriptionFormatter } = useFinancialReportRow(pageFilter?.showAccountNumber ?? false);

  React.useEffect(() => {
    const headers = ExportHelper.generateSheetHeaders(result, (_, index) => [
      ibHeaderDescription(index),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Change),
      ubHeaderDescription(index),
    ]);
    const sheet = ExportHelper.generateSheetData(result, descriptionFormatter, (col) => [
      ibFormatter(col).amount,
      amountFormatter(col).amount,
      ubFormatter(col).amount,
    ]);
    updateReportExportData(headers.concat(sheet));
  }, [result, pageFilter, descriptionFormatter]);

  React.useEffect(() => {
    reload();
  }, [pageFilter?.showAccountNumber]);

  useApplyExpandAllRows(tableRef, result, pageFilter?.expandAllRows);

  const { transactionAmountFormatter } = useTransactionAmount(filter);

  const chartsVisible = hasChartsPermissions();

  return (
    <>
      {chartsVisible ? <StandardBalanceReportCharts /> : <></>}

      <Grid item container>
        <DrillDownTable<
          WintSuperkollAPIModelFinancialReport.FinancialReportRow,
          WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn
        >
          key={reloadKey}
          tableRef={tableRef}
          rows={result}
          rowTextProperty={{
            key: "description",
            properties: {
              description: TranslationKeys.Common.Name,
              formatter: descriptionFormatter,
            },
          }}
          repeatProperties={{
            key: "columns",
            properties: {
              iB: {
                description: ibHeaderDescription,
                formatter: ibFormatter,
                alignment: "right",
                className: cssExports.repeatTitleCell,
              },
              amount: {
                description: TranslationKeys.Common.Change,
                formatter: transactionAmountFormatter,
                alignment: "right",
                className: cssExports.repeatTitleCell,
              },
              uB: {
                description: ubHeaderDescription,
                formatter: ubFormatter,
                alignment: "right",
                className: cssExports.repeatTitleCell,
              },
            },
            filterField: (repeatIndex: number) => (
              <MonthRangeSelectorBubble
                label=""
                onChange={onRepeatColumnFilterChange.bind(null, repeatIndex)}
                selection={getSelection(repeatIndex)}
                range={getRange(repeatIndex)}
                displayType={repeatIndex > 0 ? StyleEnums.DisplayType.Info : StyleEnums.DisplayType.Success}
                popperPlacement="bottom"
                quickSelections={repeatIndex > 0 ? comparisonColumnQuickSelections : primaryColumnQuickSelections}
                actions={
                  repeatIndex > 0
                    ? [
                        {
                          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.RemoveColumn),
                          icon: IconDefinitionConstants.WTrashAlt,
                          onClickHandler: onRepeatColumnDelete.bind(null, repeatIndex),
                        },
                      ]
                    : undefined
                }
              />
            ),
            addRepeatColumnField: (repeatIndex: number) => (
              <MonthRangeSelectorBubble
                label=""
                onChange={onRepeatColumnFilterChange.bind(null, repeatIndex)}
                popperPlacement="left-start"
                icon={IconDefinitionConstants.WPlus}
                className={cssExports.addNew}
                quickSelections={comparisonColumnQuickSelections}
              />
            ),
          }}
          childrenKey="children"
          rowDisplayType={FinancialReportHelper.getBalanceReportRowDisplayType}
          loading={loading}
        />
      </Grid>
    </>
  );
};

export default React.forwardRef(StandardBalanceReport);
